import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import lawyerImg from "../assets/images/hireNow.png";

const STRIPE_JS = "https://js.stripe.com/v3/";

// Load the Stripe.js script once and resolve when window.Stripe is available.
const loadStripeJs = () =>
  new Promise((resolve, reject) => {
    if (window.Stripe) return resolve(window.Stripe);
    let script = document.querySelector(`script[src="${STRIPE_JS}"]`);
    if (!script) {
      script = document.createElement("script");
      script.src = STRIPE_JS;
      script.async = true;
      document.body.appendChild(script);
    }
    script.addEventListener("load", () => resolve(window.Stripe));
    script.addEventListener("error", () => reject(new Error("Failed to load Stripe.js")));
  });

const HOW_IT_WORKS = [
  { icon: "bi-clipboard-check", title: "Provide Details", text: "Select category, jurisdiction, describe your issue and choose duration." },
  { icon: "bi-person-vcard", title: "We Find a Lawyer", text: "We match you with the most suitable and available lawyer." },
  { icon: "bi-camera-video", title: "Connect Instantly", text: "Get connected via secure video call in under 60 seconds." },
  { icon: "bi-chat-dots", title: "Get Legal Help", text: "Discuss your case and get expert legal advice." },
];

const ConsultationCall = () => {
  // Form state
  const [categoriesList, setCategoriesList] = useState([]);
  const [jurisdictionsList, setJurisdictionsList] = useState([]);
  const [packages, setPackages] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [jurisdictionId, setJurisdictionId] = useState("");
  const [caseDescription, setCaseDescription] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Checkout modal state
  const [showCheckout, setShowCheckout] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [booked, setBooked] = useState(false);

  const stripeRef = useRef(null);
  const cardRef = useRef(null);
  const clientSecretRef = useRef(null);

  // ---- Load dropdowns + packages -----------------------------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const base = process.env.REACT_APP_API_URL;
        const [cats, dropdown, pkgs] = await Promise.all([
          axios.get(`${base}/getCategories`),
          axios.get(`${base}/getDropdownData`),
          axios.get(`${base}/getCallPackages`),
        ]);
        if (cats?.data?.status) setCategoriesList(cats.data.data || []);
        if (dropdown?.data?.status) setJurisdictionsList(dropdown.data.data.jurisdictions || []);
        if (pkgs?.data?.status) {
          const list = pkgs.data.data || [];
          setPackages(list);
          const def = list.find((p) => p.is_popular) || list[0];
          if (def) setSelectedPackage(def);
        }
      } catch (e) {
        console.error("Error fetching consultation data:", e);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, []);

  // ---- OTP ----------------------------------------------------------------
  const handleSendOtp = async () => {
    if (!fullName || !email) {
      toast.error("Please provide both name and email.");
      return;
    }
    setOtpLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", fullName);
      formData.append("email", email);
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/resendOTP`, formData);
      if (res?.data?.status) {
        toast.success(res?.data?.message || "OTP sent successfully!");
      } else {
        toast.error(res?.data?.message || "Failed to send OTP.");
      }
    } catch (e) {
      toast.error("An error occurred while sending OTP.");
    } finally {
      setOtpLoading(false);
    }
  };

  useEffect(() => {
    const verifyOtp = async () => {
      if (otp.length !== 4 || !email) return;
      try {
        const formData = new FormData();
        formData.append("email", email);
        formData.append("otp", otp);
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/verifyOTP`, formData);
        if (res?.data?.status) {
          toast.success(res?.data?.message || "OTP verified successfully!");
          setOtpVerified(true);
          setAuthToken(res.data.data.auth_token);
        } else {
          toast.error(res?.data?.message || "Invalid OTP.");
          setOtpVerified(false);
          setAuthToken(null);
        }
      } catch (e) {
        toast.error("An error occurred during OTP verification.");
        setOtpVerified(false);
        setAuthToken(null);
      }
    };
    verifyOtp();
  }, [otp, email]);

  // ---- Open checkout ------------------------------------------------------
  const handleStartConnecting = () => {
    if (!categoryId || !jurisdictionId || !caseDescription.trim()) {
      toast.error("Please choose a category, jurisdiction and describe your issue.");
      return;
    }
    if (!selectedPackage) {
      toast.error("Please select a call duration.");
      return;
    }
    setShowCheckout(true);
  };

  // ---- Mount Stripe card element ------------------------------------------
  const initCard = async () => {
    if (!authToken || !selectedPackage) return;
    setPayLoading(true);
    try {
      const payData = new FormData();
      payData.append("amount", selectedPackage.price);
      payData.append("call_package_id", selectedPackage.id);
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/consultationPayment`,
        payData,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (!res?.data?.status) {
        toast.error(res?.data?.message || "Failed to start payment.");
        return;
      }
      clientSecretRef.current = res.data.data.paymentIntent;

      const Stripe = await loadStripeJs();
      stripeRef.current = Stripe(res.data.data.publishableKey);
      const elements = stripeRef.current.elements();
      const card = elements.create("card", { hidePostalCode: true });
      card.mount("#cc-card-element");
      cardRef.current = card;
      setCardReady(true);
    } catch (e) {
      toast.error("An error occurred while preparing payment.");
    } finally {
      setPayLoading(false);
    }
  };

  // ---- Pay + initiate call ------------------------------------------------
  const handlePay = async () => {
    if (!stripeRef.current || !cardRef.current || !clientSecretRef.current) {
      toast.error("Payment form is not ready yet.");
      return;
    }
    setPayLoading(true);
    try {
      const result = await stripeRef.current.confirmCardPayment(clientSecretRef.current, {
        payment_method: { card: cardRef.current, billing_details: { name: fullName, email } },
      });
      if (result.error) {
        toast.error(result.error.message || "Payment failed.");
        return;
      }
      if (result.paymentIntent?.status !== "succeeded") {
        toast.error("Payment could not be completed.");
        return;
      }

      const callData = new FormData();
      callData.append("call_package_id", selectedPackage.id);
      callData.append("category_id", categoryId);
      callData.append("jurisdiction_id", jurisdictionId);
      callData.append("case_description", caseDescription);
      callData.append("amount_paid", selectedPackage.price);
      callData.append("currency", selectedPackage.currency || "USD");
      callData.append("transaction_id", result.paymentIntent.id);
      callData.append("payment_status", "paid");

      const callRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/storeCallHistory`,
        callData,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (callRes?.data?.status) {
        setBooked(true);
      } else {
        toast.error(callRes?.data?.message || "Payment succeeded but booking failed. Please contact support.");
      }
    } catch (e) {
      toast.error("An error occurred while processing your payment.");
    } finally {
      setPayLoading(false);
    }
  };

  const closeCheckout = () => {
    setShowCheckout(false);
    setBooked(false);
    setCardReady(false);
    setOtp("");
    setOtpVerified(false);
    setAuthToken(null);
    stripeRef.current = null;
    cardRef.current = null;
    clientSecretRef.current = null;
  };

  const fmtPrice = (p) => `${p.currency || "USD"} ${Number(p.price).toFixed(0)}`;

  return (
    <div className="cc-page font-inter">
      <Seo
        title="Consultation Call | Talk to a Lawyer on Video - Legal Platform"
        description="Connect with a verified lawyer on a video call within minutes. Choose your category, jurisdiction and call duration to get instant legal assistance."
        canonicalPath={process.env.REACT_APP_BASE_PATH + "/consultation-call"}
        image="/favicon.png"
      />
      <Header />

      <main className="cc-main container py-5">
        <div className="cc-hero-grid">
          {/* ----------------------- LEFT: FORM ----------------------- */}
          <div className="cc-form-card">
            <div className="text-center">
              <div className="cc-badge-pill">
                <i className="bi bi-lightning-charge-fill" /> INSTANT LEGAL ASSISTANCE
              </div>
              <h1 className="cc-title">Connect with a lawyer in Minutes</h1>
              <p className="cc-subtitle">
                Fill in the details below and we will connect you with an available lawyer right away.
              </p>
            </div>

            <div className="cc-field">
              <label className="cc-label">Legal Category</label>
              <select className="form-select cc-select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">Select legal category</option>
                {categoriesList.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="cc-field">
              <label className="cc-label">Jurisdiction</label>
              <select className="form-select cc-select" value={jurisdictionId} onChange={(e) => setJurisdictionId(e.target.value)}>
                <option value="">Select jurisdiction</option>
                {jurisdictionsList.map((j) => (
                  <option key={j.id} value={j.id}>{j.name}</option>
                ))}
              </select>
            </div>

            <div className="cc-field">
              <label className="cc-label">Explain Your Issue</label>
              <textarea
                className="form-control cc-textarea"
                placeholder="Please describe your legal in detail..."
                maxLength={500}
                value={caseDescription}
                onChange={(e) => setCaseDescription(e.target.value)}
              />
            </div>

            <div className="cc-field">
              <label className="cc-label">Select Call Duration</label>
              <div className="cc-duration-grid">
                {packages.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    className={`cc-duration-card ${selectedPackage?.id === p.id ? "selected" : ""}`}
                    onClick={() => setSelectedPackage(p)}
                  >
                    <i className="bi bi-clock cc-duration-icon" />
                    <span className="cc-duration-title">{p.duration_minutes} Minutes</span>
                    <span className="cc-duration-price">{fmtPrice(p)}</span>
                    {p.is_popular && <span className="cc-duration-pop">Popular</span>}
                  </button>
                ))}
              </div>
            </div>

            <button type="button" className="cc-cta-btn" onClick={handleStartConnecting}>
              <i className="bi bi-camera-video-fill me-2" />
              Start Connecting with Available Lawyer
            </button>
            <div className="cc-secure-note">
              <i className="bi bi-lock-fill" /> Your information is secure and encrypted
            </div>
          </div>

          {/* ----------------------- RIGHT: HERO ----------------------- */}
          <div className="cc-hero-visual">
            <img src={lawyerImg} alt="Verified lawyer ready to help" className="cc-hero-img" />

            <div className="cc-float cc-float-online">
              <span className="cc-online-dot" />
              <div>
                <div className="cc-float-strong">42 <span>Lawyers Online</span></div>
                <div className="cc-float-sub">Ready to help you</div>
              </div>
            </div>

            <div className="cc-float cc-float-stats">
              <div className="cc-stat">
                <i className="bi bi-clock-history" />
                <div>
                  <div className="cc-stat-label">Average Connection Time</div>
                  <div className="cc-stat-strong">32 Seconds</div>
                </div>
              </div>
              <div className="cc-stat">
                <i className="bi bi-shield-check" />
                <div>
                  <div className="cc-stat-label">100% Confidential</div>
                  <div className="cc-stat-sub">Your privacy is our priority</div>
                </div>
              </div>
              <div className="cc-stat">
                <i className="bi bi-camera-video" />
                <div>
                  <div className="cc-stat-label">Secure Video Calls</div>
                  <div className="cc-stat-sub">End-to-end encrypted</div>
                </div>
              </div>
            </div>

            <div className="cc-float cc-float-sos">
              <div className="cc-sos-head"><i className="bi bi-exclamation-octagon-fill" /> SOS</div>
              <div className="cc-sos-title">Emergency Call</div>
              <div className="cc-sos-text">Connect instantly with a lawyer in emergency.</div>
              <button type="button" className="cc-sos-btn" onClick={handleStartConnecting}>
                <i className="bi bi-telephone-fill me-1" /> Call Now
              </button>
            </div>
          </div>
        </div>

        {/* ----------------------- HOW IT WORKS ----------------------- */}
        <div className="cc-how">
          <div className="cc-how-title">HOW IT WORKS</div>
          <div className="cc-how-grid">
            {HOW_IT_WORKS.map((s, i) => (
              <React.Fragment key={s.title}>
                <div className="cc-how-item">
                  <div className="cc-how-icon">
                    <i className={`bi ${s.icon}`} />
                    <span className="cc-how-num">{i + 1}</span>
                  </div>
                  <div className="cc-how-name">{s.title}</div>
                  <div className="cc-how-text">{s.text}</div>
                </div>
                {i < HOW_IT_WORKS.length - 1 && <div className="cc-how-arrow"><i className="bi bi-arrow-right" /></div>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </main>

      {/* ----------------------- CHECKOUT MODAL ----------------------- */}
      {showCheckout && (
        <div className="cc-modal-overlay" onClick={closeCheckout}>
          <div className="cc-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="cc-modal-close" onClick={closeCheckout} aria-label="Close">
              <i className="bi bi-x-lg" />
            </button>

            {booked ? (
              <div className="cc-success text-center">
                <div className="cc-success-icon"><i className="bi bi-check-lg" /></div>
                <h3 className="cc-success-title">You're all set!</h3>
                <p className="cc-success-sub">
                  Your payment was successful. We're connecting you with a verified lawyer for your
                  {" "}{selectedPackage?.duration_minutes}-minute consultation. You'll be notified the
                  moment a lawyer is ready.
                </p>
                <button type="button" className="cc-cta-btn mt-2" onClick={closeCheckout}>Done</button>
              </div>
            ) : (
              <>
                <h3 className="cc-modal-title">Confirm &amp; Pay</h3>
                <div className="cc-summary">
                  <div className="cc-summary-row">
                    <span>Consultation</span>
                    <span>{selectedPackage?.title} ({selectedPackage?.duration_minutes} min)</span>
                  </div>
                  <div className="cc-summary-row cc-summary-total">
                    <span>Total</span>
                    <span>{selectedPackage ? fmtPrice(selectedPackage) : "-"}</span>
                  </div>
                </div>

                <div className="row g-2 mt-1">
                  <div className="col-md-6">
                    <input type="text" className="form-control cc-input" placeholder="Full Name"
                      value={fullName} onChange={(e) => setFullName(e.target.value)} disabled={otpVerified} />
                  </div>
                  <div className="col-md-6">
                    <input type="email" className="form-control cc-input" placeholder="Email Address"
                      value={email} onChange={(e) => setEmail(e.target.value)} disabled={otpVerified} />
                  </div>
                </div>

                {!otpVerified ? (
                  <div className="postq-otp-block mt-2">
                    <button type="button" className="postq-otp-btn w-100" onClick={handleSendOtp} disabled={otpLoading}>
                      {otpLoading ? "Sending..." : "Send OTP"}
                    </button>
                    <input type="text" className="postq-otp-input" placeholder="Enter OTP" value={otp}
                      onChange={(e) => { if (/^\d{0,4}$/.test(e.target.value)) setOtp(e.target.value); }} maxLength="4" />
                  </div>
                ) : (
                  <div className="cc-verified mt-2"><i className="bi bi-patch-check-fill" /> Email verified</div>
                )}

                <div className="cc-modal-sub mt-3">Payment</div>
                {!cardReady ? (
                  <button type="button" className="cc-cta-btn" onClick={initCard} disabled={!otpVerified || payLoading}>
                    {payLoading ? "Preparing..." : `Enter Card Details — ${selectedPackage ? fmtPrice(selectedPackage) : ""}`}
                  </button>
                ) : (
                  <>
                    <div id="cc-card-element" className="cc-card-box" />
                    <button type="button" className="cc-cta-btn mt-3" onClick={handlePay} disabled={payLoading}>
                      <i className="bi bi-camera-video-fill me-2" />
                      {payLoading ? "Processing..." : `Pay & Initiate Call — ${selectedPackage ? fmtPrice(selectedPackage) : ""}`}
                    </button>
                  </>
                )}
                <div className="cc-secure-note"><i className="bi bi-lock-fill" /> Secured by Stripe</div>
              </>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ConsultationCall;
